/** Extends the built in Date class with static formatting methods
    @see http://www.loganfranken.com/blog/864/es6-everyday-extending-built-in-classes/
    @description Caution, there be wolves!
    @see http://kangax.github.io/compat-table/es6/
    @see https://stackoverflow.com/questions/563406/add-days-to-javascript-date
*/
export default class DATE extends Date {
    /** Returns Date plus n seconds
        @static
        @param {number} seconds Number of Seconds to Add
        @returns {ThisType} Date
    */
    addSeconds(seconds) {
        this.setSeconds(this.getSeconds() + seconds);
        return this;
    }
    /** Returns Date plus n minutes
        @static
        @param {number} minutes Number of Minutes to Add
        @returns {ThisType} Date
    */
    addMinutes(minutes) {
        this.setMinutes(this.getMinutes() + minutes);
        return this;
    }
    /** Returns Date plus n hours
        @static
        @param {number} hours Number of Hours to Add
        @returns {ThisType} Date
    */
    addHours(hours) {
        this.setHours(this.getHours() + hours);
        return this;
    }
    /** Returns Date plus n days
        @static
        @param {number} days Number of Days to Add
        @returns {ThisType} Date
    */
    addDays(days) {
        this.setDate(this.getDate() + days);
        return this;
    }
    /** Returns Date plus n months
        @static
        @param {number} months Number of Months to Add
        @returns {ThisType} Date
    */
    addMonths(months) {
        //this.setMonth(this.getMonth() + months + 1); // Month is zero indexed
        this.setMonth(this.getMonth() + months); // Was this patched?
        return this;
    }
    /** Returns Date plus n years
        @static
        @param {number} years Number of Years to Add
        @returns {ThisType} Date
    */
    addYears(years) {
        this.setFullYear(this.getFullYear() + years);
        return this;
    }
    /** Returns the UTC offset in minutes
        The JS Date implemention has an odd implementation of this which returns the inverse
        of what you'd expect.
        @see https://stackoverflow.com/questions/21102435/why-does-javascript-date-gettimezoneoffset-consider-0500-as-a-positive-off
        @static
        @returns {number} UTC Offset in Minutes
    */
    getUTCOffset() {
        return this.getTimezoneOffset() * -1;
    }
    /** Returns the UTC offset in minutes
        The JS Date implemention has an odd implementation of this which returns the inverse
        of what you'd expect.
        @see https://stackoverflow.com/questions/21102435/why-does-javascript-date-gettimezoneoffset-consider-0500-as-a-positive-off
        @static
        @returns {number} UTC Offset in Hours
    */
    getUTCOffsetHours() {
        return this.getTimezoneOffset() * -1 / 60;
    }
    /** Returns the last day of month for the given date
        @static
        @returns {ThisType} Date
    */
    getBOM() {
        this.setDate(1);
        return this;
    }
    /** Returns the last day of month for the given date
        @static
        @returns {ThisType} Date
    */
    getEOM() {
        this.setDate(1);
        this.addMonths(1);
        this.addDays(-1);
        return this;
    }
    /** Returns ISO8601 formatted UTC Date String yyyy-mm-dd
        @static
        @param {number} offset Hour Difference between UTC timestamp and target
        @returns {string} ISO8601 Date String
    */
    toISO8601(offset = 0) {
        return this.addHours(offset).toISOString().substring(0, 10);
    }
    /** Returns ISO8601 formatted UTC DateTime String yyyy-mm-dd HH:mm:ss
        @static
        @param {number} offset Hour Difference between UTC timestamp and target
        @returns {string} ISO8601 DateTime String
    */
    toISO8601DT(offset = 0) {
        return this.addHours(offset).toISOString().substring(0, 19).replace('T', ' ');
    }
    /** Returns the difference in Days between this date and a given DATE
        @param {Date} date Date to subtract from this Date
        @returns {number} Number of days between this and given date
    */
    dayDiff(date) {
        return (this.getTime() - date.getTime()) / (1000 * 3600 * 24);
    }
    /** Returns the difference in Hours between this date and a given DATE
        @param {Date} date Date to subtract from this Date
        @returns {number} Number of hours between this and given date
    */
    hourDiff(date) {
        return (this.getTime() - date.getTime()) / (1000 * 3600);
    }
    /** Returns the difference in Minutes between this date and a given DATE
        @param {Date} date Date to subtract from this Date
        @returns {number} Number of minutes between this and given date
    */
    minDiff(date) {
        return (this.getTime() - date.getTime()) / (1000 * 3600) * 60;
    }
}
